import { useState, useEffect, useContext, useCallback } from "react";
import styles from "./styles.module.css";
import DataTable from "../../utils/DataTable";
import SkeletonLoading from "../../utils/Loading/SkeletonLoading";
import EmptyState from "../../utils/EmptyState/EmptyState";
import ShopContext from "../../../context/ShopContext";
import DateTimeHelpers from "../../../helpers/DateTimeHelpers";
import toast from "react-hot-toast";
import { Button, TextField, InputAdornment, IconButton } from "@mui/material";
import PrinterImg from "images/svg/printer.svg";
import SearchIcon from "images/svg/search.svg";
import { INVOICE_FORMATS } from "const";
import * as ReportHelper from "helpers/ReportHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as ShopsHelper from "helpers/ShopsHelper";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";

export default function CategorySalesReport({ shiftId }) {
  const { shop } = useContext(ShopContext);

  const shopProfile = ShopsHelper.getCachedShopProfile();
  const currencySymbol = shopProfile?.currency_symbol;

  const todayYMD = DateTimeHelpers.getDateYMD(new Date());

  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCategoryReport = useCallback(async () => {
    try {
      setLoading(true);
      const resData = await ReportHelper.getCategorySalesSummaryForShift(shiftId);
      setRows(resData || []);
    } catch (error) {
      console.error("Failed to fetch Category Sales Report:", error);
      toast.error("Failed to load Category Sales Report");
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    fetchCategoryReport();
  }, [fetchCategoryReport]);

  const filteredRows = rows
    .filter((item) => (item.name || "").toLowerCase().includes(searchQuery.toLowerCase()))
    .map((item, index) => ({ ...item, slNo: index + 1 }));

  const totalAmountSum = filteredRows.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
  const totalPurchaseSum = filteredRows.reduce((sum, item) => sum + (Number(item.purchaseAmount) || 0), 0);
  const totalDamageSum = filteredRows.reduce((sum, item) => sum + (Number(item.damageAmount) || 0), 0);

  const columns = [
    {
      label: "Sl.No",
      id: "slNo",
      type: "text",
      width: "70px",
    },
    {
      label: "Name",
      id: "name",
      type: "text",
      width: "600px",
    },
    {
      label: "Sales Qty",
      id: "salesQuantity",
      type: "callback",
      viewRender: (item) => `${NumberFormatHelper.quantityRoundOff(item.salesQuantity ?? 0)}`,
    },
    {
      label: "Sales Amount",
      id: "total",
      type: "callback",
      viewRender: (item) => `${currencySymbol} ${NumberFormatHelper.amountRoundOff(item.total ?? 0)}`,
    },
    {
      label: "Purchase Amount",
      id: "purchaseAmount",
      type: "callback",
      viewRender: (item) => `${currencySymbol} ${NumberFormatHelper.amountRoundOff(item.purchaseAmount ?? 0)}`,
    },
    {
      label: "Damage Amount",
      id: "damageAmount",
      type: "callback",
      viewRender: (item) => `${currencySymbol} ${NumberFormatHelper.amountRoundOff(item.damageAmount ?? 0)}`,
    },
  ];

  const handlePrint = async () => {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;
    const todayFormatted = DateTimeHelpers.formatISODateToDMY(todayYMD);

    let reportText;
    if (
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC ||
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE
    ) {
      reportText = await ReportHelper.getCategorySalesMultiThreeInch(
        filteredRows,
        shop,
        todayFormatted,
        todayFormatted,
      );
    } else {
      reportText = await ReportHelper.getCategorySalesReport(filteredRows, shop, todayFormatted, todayFormatted);
    }
    await ReportHelper.printReport({ reportText, fallbackFunction: window.print });
  };

  return (
    <div className={styles.container}>
      <div style={{ height: "100%" }}>
        <div className={styles.headerSection}>
          <div>
            <h2 className={styles.title}>Category Sales Summary</h2>
            <h3 className={styles.subTitle}>
              Category Sales Report for {DateTimeHelpers.formatISODateToDMY(todayYMD)}
            </h3>
          </div>
          <div className={styles.btnSection}>
            <TextField
              placeholder="Search by name"
              size="small"
              className={styles.searchField}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" style={{ padding: "0" }}>
                      <img src={SearchIcon} alt="Search" className={styles.icon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              className={styles.printBtn}
              onClick={handlePrint}
              startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
            >
              Print
            </Button>
          </div>
        </div>

        <div className={styles.tableSection}>
          {loading ? (
            <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
          ) : filteredRows.length > 0 ? (
            <DataTable
              columns={columns}
              rows={filteredRows}
              rowKey="id"
              summary={{
                slNo: "Total",
                name: "",
                total: `${currencySymbol} ${NumberFormatHelper.amountRoundOff(totalAmountSum)}`,
                purchaseAmount: `${currencySymbol} ${NumberFormatHelper.amountRoundOff(totalPurchaseSum)}`,
                damageAmount: `${currencySymbol} ${NumberFormatHelper.amountRoundOff(totalDamageSum)}`,
              }}
            />
          ) : (
            <EmptyState title="Category Sales Report" />
          )}
        </div>
      </div>
    </div>
  );
}
